/**
 * "Ask <desk>" on a case file's outstanding item (kanban 52a4147a).
 *
 * Jess, on INV-00006: the Ask buttons that sat on the trigger's Do next
 * were gone once the case was opened, and she wanted one "right next to
 * the line item for that outstanding item" to send "clear SHERW01 credit
 * hold" to Laura. The item keeps the desk Merlin named in its trailing
 * parentheses, so the button can name them; the owner toggle is the only
 * hint when it does not.
 */
import { groupSentAsks, type DeskAskGroup } from '../../pages/quality-triage/merlinBrief';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { DoorCaseTaskRead } from '../../services/qcpInvestigations';
import { isDraft } from '../quality/askKind';
import { leadingDesk, splitDesk } from '../quality/deskSplit';

export interface TaskAskDesk {
  /** The desk the modal opens for: a name, "customer", or '' for nobody named. */
  desk: string;
  /** What the button says: "Ask Laura", "Ask the customer", "Ask a colleague". */
  label: string;
}

/**
 * The desk a row's Ask goes to and what its button says. ``deskHint`` is
 * what the host knows from elsewhere — the desk named on the Do next step
 * the item restates — and stands in when the label itself names nobody.
 */
export function taskAskDesk(
  task: Pick<DoorCaseTaskRead, 'label' | 'owner'>,
  deskHint: string | null | undefined = '',
): TaskAskDesk {
  // A customer's item is never asked (taskCanAsk says no; the row shows a
  // recommendation, kanban d3915b86); its desk stays "customer" for the
  // rail's wording, never the contact its label names first.
  const lead = task.owner === 'customer' ? '' : leadingDesk(task.label);
  const named = splitDesk(task.label).desk || lead || (deskHint ?? '').trim();
  if (named) return { desk: named, label: `Ask ${named}` };
  if (task.owner === 'customer') return { desk: 'customer', label: 'Ask the customer' };
  return { desk: '', label: 'Ask a colleague' };
}

/** The item without its desk — what the ask's message quotes. */
export function taskAskStep(task: Pick<DoorCaseTaskRead, 'label'>): string {
  return splitDesk(task.label).text;
}

/** The asks sent from this row, one pill per send. Drafts are not pills. */
export function taskAskGroups(
  task: Pick<DoorCaseTaskRead, 'id'>,
  asks: ReadonlyArray<DoorDataRequestRead>,
): DeskAskGroup[] {
  return groupSentAsks(asks.filter((r) => r.task_id === task.id && !isDraft(r)));
}

/**
 * Whether the row offers Ask: still open, not on our own desk (nobody to
 * ask but ourselves) unless it names a teammate first ("QC lab (Jake): …",
 * UX round 2026-10-13), not the customer's move (quality reaches customers
 * through the sales rep, never from here — the row shows a recommendation
 * instead, kanban d3915b86), and nothing sent from it still waiting — the
 * same "steps aside while an ask is out" rule the Do next desks follow.
 */
export function taskCanAsk(
  task: Pick<DoorCaseTaskRead, 'status' | 'owner'> & Partial<Pick<DoorCaseTaskRead, 'label'>>,
  groups: ReadonlyArray<Pick<DeskAskGroup, 'state'>>,
): boolean {
  if (task.owner === 'customer') return false;
  const someoneToAsk = task.owner !== 'us' || !!leadingDesk(task.label ?? '');
  return task.status === 'open' && someoneToAsk && !groups.some((g) => g.state === 'waiting');
}
